"use client";
import { configureStore } from "@reduxjs/toolkit";
import { apiSlice } from "./api/apiSlice.js";
import authSlice from "./auth/authSlice.js";
import langSlice from "./langSlice";

export const store = configureStore({
  reducer: {
    [apiSlice.reducerPath]: apiSlice.reducer,
    auth: authSlice,
    lang: langSlice,

  },
  devTools: false,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(apiSlice.middleware),
});

// call the load user function on every page load
const initializeApp = async () => {
  await store.dispatch(
    apiSlice.endpoints.loadUser.initiate({}, { forceRefetch: true })
  );
};

initializeApp();

// ─── Cross-tab login identity sync ──────────────────────────────────────────
// Redux (this store) is per-tab and in-memory; the access_token cookie is shared
// by every tab of the browser. When a second school logs in from another tab,
// this tab keeps SHOWING the first school while every request it sends runs as
// the second one — the Ek Ped 3.0 form and the certificate then mix the two
// schools (seen 2026-09-25: UDISE 5040108601 vs 5040129301). Each tab publishes
// the identity it is showing; a tab that learns the browser identity changed
// reloads, which re-reads the cookie via /me.
const IDENTITY_KEY = "ec_active_identity";
const RELOAD_GUARD_KEY = "ec_identity_reload_at";

const identityOf = (user) => {
  if (!user || typeof user !== "object") return "";
  const udise = String(user.udise_id ?? user.udiseId ?? "").replace(/\D/g, "").replace(/^0+/, "");
  const id = user._id || user.id || "";
  if (!udise && !id) return "";
  return `${udise}|${user.role ?? ""}|${id}`;
};

const readStored = () => { try { return localStorage.getItem(IDENTITY_KEY) ?? ""; } catch { return ""; } };
const writeStored = (v) => { try { if (readStored() !== v) localStorage.setItem(IDENTITY_KEY, v); } catch { /* storage unavailable */ } };

const reloadForIdentityChange = (why) => {
  // At most one identity reload per 10 s per tab, so two tabs can't ping-pong.
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < 10000) { console.warn("identity changed again within 10s, not reloading:", why); return; }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch { /* ignore */ }
  console.warn("Login identity changed in this browser — reloading tab:", why);
  window.location.reload();
};

let lastPublished = null;
store.subscribe(() => {
  const auth = store.getState().auth;
  if (!auth?.authChecked) return; // don't publish until /me has settled
  const mine = identityOf(auth.user);
  if (mine === lastPublished) return;
  lastPublished = mine;
  writeStored(mine);
});

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== IDENTITY_KEY) return;
    const auth = store.getState().auth;
    if (!auth?.authChecked) return;
    const mine = identityOf(auth.user);
    const theirs = e.newValue ?? "";
    if (theirs !== mine) reloadForIdentityChange(`storage: ${mine || "(none)"} → ${theirs || "(none)"}`);
  });
  // Safety net for browsers/tabs that missed the storage event: re-check when
  // the tab comes back to the foreground.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    const auth = store.getState().auth;
    if (!auth?.authChecked) return;
    const mine = identityOf(auth.user);
    const stored = readStored();
    if (stored && stored !== mine) reloadForIdentityChange(`visible: ${mine || "(none)"} → ${stored}`);
  });
}
